import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Mail, CheckCircle, XCircle, Loader2 } from "lucide-react";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Resend state
  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setVerifying(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyEmail = async () => {
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed. The link may have expired.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResent(true);
    } catch (err) {
      toast.error("Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-forest" />
              <p className="text-muted-foreground">Verifying your email...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-6 h-6 text-forest" />
            </div>
            <CardTitle className="font-display text-2xl">Email Verified!</CardTitle>
            <CardDescription>
              Your email has been verified. You can now log in to your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button className="w-full bg-forest hover:bg-forest/90">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-6 h-6 text-red-600" />
            </div>
            <CardTitle className="font-display text-2xl">Verification Failed</CardTitle>
            <CardDescription className="text-red-600">
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Need a new verification link? Enter your email below.
            </p>
            {resent ? (
              <div className="text-center p-4 bg-forest/5 rounded-lg">
                <CheckCircle className="w-5 h-5 text-forest mx-auto mb-2" />
                <p className="text-sm text-forest">Verification email sent! Check your inbox.</p>
              </div>
            ) : (
              <form onSubmit={handleResend} className="space-y-3">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  className="w-full"
                  disabled={resending}
                >
                  {resending ? "Sending..." : "Resend Verification Email"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // No token provided - show resend form
  return (
    <div className="min-h-screen bg-sand flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-forest/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-6 h-6 text-forest" />
          </div>
          <CardTitle className="font-display text-2xl">Verify Your Email</CardTitle>
          <CardDescription>
            Enter your email to receive a new verification link.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {resent ? (
            <div className="text-center p-4 bg-forest/5 rounded-lg">
              <CheckCircle className="w-5 h-5 text-forest mx-auto mb-2" />
              <p className="text-sm text-forest">Verification email sent! Check your inbox.</p>
            </div>
          ) : (
            <form onSubmit={handleResend} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-forest hover:bg-forest/90"
                disabled={resending}
              >
                {resending ? "Sending..." : "Send Verification Email"}
              </Button>
            </form>
          )}
          <Link to="/login">
            <Button variant="ghost" className="w-full">
              Back to Login
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
